import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["duration", "durationDisplay", "contractType", "hours", "labelFull", "labelPart", "memberType", "hourlyRate"]
  static values = {
    startTime: String,
    endTime: String
  }

  connect() {
    // Find start and end time inputs in the form (for shift duration calculation)
    const form = this.element.closest('form')
    if (form) {
      const startInput = form.querySelector('input[name*="[start_time]"]')
      const endInput = form.querySelector('input[name*="[end_time]"]')

      if (startInput && endInput) {
        startInput.addEventListener('change', () => this.calculate())
        endInput.addEventListener('change', () => this.calculate())
      }
    }

    // Calculate initial duration on connect
    this.calculate()

    // Update hours display on connect (for contract type/hours sync)
    this.updateHoursDisplay()
  }

  calculate() {
    const form = this.element.closest('form')
    if (!form) return

    const startInput = form.querySelector('input[name*="[start_time]"]')
    const endInput = form.querySelector('input[name*="[end_time]"]')
    const durationInput = this.hasDurationTarget ? this.durationTarget : form.querySelector('input[name*="[duration_hours]"]')

    if (!startInput || !endInput || !durationInput) return

    const startValue = startInput.value
    const endValue = endInput.value

    if (!startValue || !endValue) return

    // Parse time values (HH:MM format)
    const [startHours, startMinutes] = startValue.split(':').map(Number)
    const [endHours, endMinutes] = endValue.split(':').map(Number)

    const startSeconds = startHours * 3600 + startMinutes * 60
    const endSeconds = endHours * 3600 + endMinutes * 60

    // Handle overnight shifts (e.g., 23:00 - 08:00)
    let duration
    if (endSeconds < startSeconds) {
      duration = (86400 - startSeconds + endSeconds) / 3600.0
    } else {
      duration = (endSeconds - startSeconds) / 3600.0
    }

    // Update hidden field with numeric value
    durationInput.value = duration.toFixed(2)

    // Update display field with user-friendly format
    if (this.hasDurationDisplayTarget) {
      const displayText = duration === 1 ? "1 hour" : `${duration.toFixed(1).replace(/\.0$/, '')} hours`
      this.durationDisplayTarget.value = displayText
    }
  }

  // Contract type changed - update hours value and labels
  contractTypeChanged() {
    if (!this.hasContractTypeTarget || !this.hasHoursTarget) return

    const isPartTime = this.contractTypeTarget.value === "part"

    // Always update hours to default value when contract type changes
    if (isPartTime) {
      this.hoursTarget.value = "24"
    } else {
      this.hoursTarget.value = "40"
    }

    this.updateLabels(isPartTime)
  }

  updateHoursDisplay() {
    if (!this.hasContractTypeTarget) return
    const isPartTime = this.contractTypeTarget.value === "part"
    this.updateLabels(isPartTime)
  }

  updateLabels(isPartTime) {
    if (this.hasLabelFullTarget && this.hasLabelPartTarget) {
      this.labelFullTarget.classList.toggle("hidden", isPartTime)
      this.labelPartTarget.classList.toggle("hidden", !isPartTime)
    }
  }

  // Extern (flexpool) toggle changed — show/hide hourly rate input
  memberTypeChanged() {
    if (!this.hasMemberTypeTarget || !this.hasHourlyRateTarget) return

    const isExternal = this.memberTypeTarget.checked
    this.hourlyRateTarget.classList.toggle("hidden", !isExternal)

    if (!isExternal) {
      this.hourlyRateTarget.value = ""
    }
  }
}
